import { computed, onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import reportService, { type WarehousePurchaseReportItem } from "@/services/report";
import supplierService, { type Supplier } from "@/services/supplier";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type PurchaseReportFilters = {
  warehouse_id: number | null;
  supplier_id: number | null;
  status: string;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): PurchaseReportFilters => ({
  warehouse_id: null,
  supplier_id: null,
  status: "",
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function usePurchaseReport() {
  const { t } = useI18n();
  const purchases = ref<WarehousePurchaseReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const suppliers = ref<Supplier[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<PurchaseReportFilters>(createInitialFilters());

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t("purchase_report_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    locale: { items_per_page: t("purchase_report_page.items_per_page") },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
    supplier_id: filters.supplier_id || undefined,
    status: filters.status || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, supplierResults] = await Promise.all([
        warehouseService.getWarehouses(),
        supplierService.getSuppliers({ per_page: 1000 }),
      ]);

      warehouses.value = warehouseResults;
      suppliers.value = supplierResults.data ?? [];
    } catch (error) {
      console.error("Error loading purchase report filters:", error);
    }
  };

  const fetchPurchaseReports = async () => {
    loading.value = true;
    try {
      const response = await reportService.getPurchaseReports(queryParams.value);
      purchases.value = response.data;
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching purchase reports:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_report_page.load_failed"),
        text: t("purchase_report_page.unable_to_load_purchase_reports"),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchPurchaseReports();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchPurchaseReports();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchPurchaseReports();
  };

  const exportPurchaseReports = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportPurchaseReports({
        search_query: searchQuery.value,
        warehouse_id: filters.warehouse_id || undefined,
        supplier_id: filters.supplier_id || undefined,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `purchase_report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting purchase reports:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_report_page.export_failed"),
        text: t("purchase_report_page.unable_to_export_purchase_reports"),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchPurchaseReports();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchPurchaseReports();
  });

  return {
    purchases,
    warehouses,
    suppliers,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    total,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportPurchaseReports,
  };
}
